<template>
  <section>
    <!-- Address Line 1 -->
    <q-field
      :label="!hideLabel ? this.$t('address') : ''"
      :labelWidth="labelWidth"
      :icon="showIcon ? 'location_on' : undefined"
      :error="errors.has('addressLine1_' + id)"
      :error-label="errors.first('addressLine1_' + id)"
    >
      <q-search
        :ref="'addressLine1_' + id"
        v-model="address.addressLine1"
        v-validate.disable="validationRules"
        :name="'addressLine1_' + id"
        :data-vv-as="$t('address_line_1')"
        :placeholder="$t('address_line_1')"
      >
        <infinite-autocomplete
          @fetch="search"
          @search="search"
          @selected="selected"
          :debounce="defaultSearchDebounce"
          :min-characters="0"
          :max-results="6"
        />
      </q-search>
    </q-field>

    <template v-if="address.addressLine1 || expanded">
      <q-field :inset="inset" :labelWidth="labelWidth">
        <div class="row gutter-sm">
          <!-- Postal Code -->
          <div class="col-sm-4 col-12">
            <q-field
              class="no-margin"
              :error="errors.has('postalCode_' + id)"
              :error-label="errors.first('postalCode_' + id)"
            >
              <q-input
                v-model="address.postalCode"
                v-validate.disable="'required|min:1|max:255'"
                :name="'postalCode_' + id"
                :data-vv-as="$t('postal_code')"
                :placeholder="$t('postal_code')"
              />
            </q-field>
          </div>

          <!-- Locality -->
          <div class="col-sm-4 col-12">
            <q-field
              class="no-margin"
              :error="errors.has('locality_' + id)"
              :error-label="errors.first('locality_' + id)"
            >
              <q-input
                v-model="address.locality"
                v-validate.disable="'required|min:1|max:255'"
                :name="'locality_' + id"
                :data-vv-as="$t('locality')"
                :placeholder="$t('locality')"
              />
            </q-field>
          </div>

          <!-- Country Code -->
          <div class="col-sm-4 col-12">
            <q-field
              class="no-margin"
              :error="errors.has('countryCode_' + id)"
              :error-label="errors.first('countryCode_' + id)"
            >
              <country-autocomplete
                v-model="address.countryCode"
                v-validate.disable="'required|country'"
                :data-vv-as="$t('country_code')"
                :name="'countryCode_' + id"
              />
            </q-field>
          </div>
        </div>
      </q-field>

      <!-- Address Label -->
      <q-field
        v-if="label"
        :inset="inset"
        :error="errors.has('label_' + id)"
        :error-label="errors.first('label_' + id)"
      >
        <label-input
          v-model="address.label"
          v-validate.disable="'max:255'"
          :name="'label_' + id"
          labelList="address"
        />
      </q-field>
    </template>
  </section>
</template>

<script>
  import { AddressSuggestion } from 'src/api/AddressSuggestion'
  import CountryAutocomplete from 'components/Common/Addressing/CountryAutocomplete/CountryAutocomplete.vue'
  import InfiniteAutocomplete from 'components/Common/InfiniteAutocomplete.vue'
  import LabelInput from 'components/Common/Inputs/LabelInput.vue'
  import debounceMixin from 'src/utils/mixins/debounce'

  export default {
    inject: ['$validator'],

    props: {
      /**
       * Used for v-model
       */
      value: {
        type: [Object],
        required: true
      },

      /**
       * Id of the input. Required for multiple address inputs per page
       */
      id: {
        type: Number,
        default: 0
      },

      /**
       * Indicate if the address is required
       */
      required: {
        type: Boolean,
        default: false
      },

      /**
       * Indicate if the input should be expanded by default
       */
      expanded: {
        type: Boolean,
        default: false
      },

      /**
       * Indicate if the label input should be visible
       */
      label: {
        type: Boolean,
        default: false
      },

      /**
       * Indicate if the label of the address line field should be visible
       */
      hideLabel: {
        type: Boolean,
        default: false
      },

      /**
       * Indicate if the field icon should be visible
       */
      showIcon: {
        type: Boolean,
        default: false
      },

      /**
       * The label width of the fields
       */
      labelWidth: {
        type: Number,
        default: 2
      },

      /**
       * Used to suggest addresses before the users start to type
       */
      searchHint: {
        type: String
      }
    },

    data () {
      return {
        address: this.value
      }
    },

    computed: {
      validationRules () {
        return this.required ? 'required|min:1|max:255' : 'min:1|max:255'
      },

      inset () {
        if (this.showIcon && !this.hideLabel) {
          return 'all'
        }

        return this.showIcon ? 'icon' : !this.hideLabel ? 'label' : null
      }
    },

    methods: {
      focus () {
        this.$refs['addressLine1_' + this.id].focus()
      },

      search (term, done) {
        term = term || this.searchHint

        if (typeof done !== 'function') {
          return
        }

        if (!term) {
          return done([])
        }

        (new AddressSuggestion())
          .search(term)
          .then(response => {
            done(response.data.items.map(item => {
              if (!item.address || !item.address.street) {
                return null
              }

              let addressLine = [item.address.street, item.address.houseNumber].join(' ')

              return {
                label: addressLine,
                sublabel: [item.address.postalCode, item.address.city].join(' '),
                value: addressLine,
                address: item.address
              }
            })
              .filter(item => !!item)
              .filter(item => item.address.countryCode === 'DEU'))
          })
          .catch(() => {
            done([])
          })
      },

      selected (item) {
        this.address.postalCode = item.address.postalCode
        this.address.locality = item.address.city
        // HERE is instructed to only return places in Germany
        // If this ever changes the three letter country codes returned by here
        // need to be mapped to two letter ones.
        // The mapping is already defined in mapping.json but loading this file would
        // be complete unnecessary at the moment
        this.address.countryCode = 'DE'
      }
    },

    watch: {
      value: {
        handler: function (newAddress) {
          this.address = newAddress
        },
        deep: true
      },

      address: {
        handler: function (newAddress) {
          this.$emit('input', newAddress)
        },
        deep: true
      }
    },

    components: {
      CountryAutocomplete,
      InfiniteAutocomplete,
      LabelInput
    },

    mixins: [
      debounceMixin
    ]
  }
</script>
